// HOW-SLOTS-WEB-AGENDA-TASKS (2026-07-09): real Agenda + Tasks sections wired to the LIVE dev
// backend (/v1/coordination-objects/{id}/agenda-items, /tasks -- see
// F:\AI_Data\projects\03-coowe-product\_planning\how-slots-backend-artifacts\
// FE-reimplement-teeup-webversion-brief.md). Supersedes the 2026-07-03 stub (honest-empty,
// non-wired placeholder) that lived at this path -- kept its visual language (SectionHeader /
// ExpandedSection card chrome, the Ale-ported color tokens) so the CANONICAL-DETAIL layout in
// TeeupPageContent.js is unaffected.
//
// RESILIENCE (plan section 4c, the hard acceptance criterion): both sections gate on
// GET /v1/capabilities (BE-7) via useAgendaTasksCapability() below -- NOT on interpreting a 404.
// Before the check settles, or if it settles false (unreachable/erroring/feature-off), the
// section renders NOTHING (not even the card chrome) -- the TeeUp detail page around it is
// unaffected either way. No agenda/tasks call is ever awaited by the page shell; each section
// loads its own data lazily off its own effect.
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"

import { useDispatch, useSelector } from "react-redux"

import { ensureCapabilitiesChecked } from "@actions/capabilitiesActions"
import {
  addAgendaItem,
  loadAgendaItems,
  markAgendaItemCovered,
  reorderAgenda,
  setAgendaItemPeople,
} from "@actions/agendaActions"
import {
  addTask,
  claimTaskThunk,
  loadTasks,
  setTaskPeople,
  toggleTaskComplete,
} from "@actions/tasksActions"
import { selectActiveTeeupId } from "@selectors/activeTeeup"
import { selectAgendaCapable, selectTasksCapable } from "@selectors/capabilities"
import { selectActiveTeeupAgendaItems } from "@selectors/agenda"
import { selectActiveTeeupTasks } from "@selectors/tasks"
import { selectTeeupPeople } from "@selectors/teeups"
import { selectUserId } from "@selectors/user"
import PeopleAvatar from "@components/PeopleAvatar"
import { aliasId, unaliasId } from "@utils/devBackendShim"

// ---- shared card chrome (unchanged from the prior stub) -----------------------------------
const SectionHeader = ({ label, count }: { label: string; count?: number }) => (
  <div className="mb-2 mt-6 flex items-center justify-between px-1">
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9AA2B1]">
      {label}
    </span>
    {typeof count === "number" && (
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9AA2B1]">
        {count}
      </span>
    )}
  </div>
)

const Card = ({
  testId,
  children,
}: {
  testId: string
  children: ReactNode
}) => (
  <div
    data-testid={testId}
    className="rounded-[13px] border border-[#E6EBF3] bg-white px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
    {children}
  </div>
)

const AddButton = ({
  testId,
  label,
  onClick,
}: {
  testId: string
  label: string
  onClick: () => void
}) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="rounded-full border border-[#7FB1E3] bg-[#EAF2FC] px-3.5 py-1.5 text-[13px] font-semibold text-[#4F7DB3] hover:bg-[#DCEAFA]">
    {label}
  </button>
)

// `map` is either selectTeeupPeople (keyed by aliasId(user_id) -- used for tasks' assignee_id) or
// the participantId-keyed derivation below (used for agenda's owner_participant_id -- a DIFFERENT
// id space, see devBackendShim.js's devParticipantToBetaUser participantId field).
const avatarProps = (map: Record<string, any>, backendId?: string | null) => {
  if (!backendId) return { avatar: "", name: "" }
  const person = map[aliasId(backendId)]
  return { avatar: person?.avatar || "", name: person?.name || "" }
}

// WU-P2-FE-a (2026-07-09): people-picker (assign/reassign/clear owner + toggle involved), shared
// by AgendaRow + TaskRow. `options` are pre-built per-section with the id-space-correct key
// already selected (participantId for agenda, user id for tasks) -- see AgendaSection/
// TasksSection below.
type PickerOption = { key: number; avatar: string; name: string }

const PeoplePickerPopover = ({
  options,
  currentOwnerKey,
  involvedKeys,
  onSelectOwner,
  onClear,
  onToggleInvolved,
  onClose,
}: {
  options: PickerOption[]
  currentOwnerKey: number | null
  involvedKeys: Set<number>
  onSelectOwner: (key: number) => void
  onClear: () => void
  onToggleInvolved: (key: number) => void
  onClose: () => void
}) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener("mousedown", onDocMouseDown)
    return () => document.removeEventListener("mousedown", onDocMouseDown)
  }, [onClose])

  return (
    <div
      ref={ref}
      data-testid="people-picker-popover"
      className="absolute right-0 top-7 z-20 w-56 rounded-[10px] border border-[#E6EBF3] bg-white p-1.5 shadow-[0_4px_14px_rgba(0,0,0,0.08)]">
      {currentOwnerKey != null && (
        <button
          type="button"
          data-testid="people-picker-clear"
          onClick={() => {
            onClear()
            onClose()
          }}
          className="flex w-full items-center rounded-lg px-2 py-1.5 text-left text-[12.5px] font-medium text-[#C0603B] hover:bg-[#F5F7FA]">
          Clear
        </button>
      )}
      <div className="max-h-52 overflow-y-auto">
        {options.map(o => (
          <div
            key={o.key}
            className="flex items-center gap-1.5 rounded-lg px-1 py-1 hover:bg-[#F5F7FA]">
            <button
              type="button"
              data-testid={`people-picker-option-${unaliasId(o.key)}`}
              onClick={() => {
                onSelectOwner(o.key)
                onClose()
              }}
              className="flex flex-1 items-center gap-2 px-1 py-0.5 text-left">
              <PeopleAvatar
                avatar={o.avatar}
                name={o.name}
                size={20}
                className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-[#EAF2FC] text-[9px] font-semibold text-[#4F7DB3]"
              />
              <span className="text-[12.5px] text-[#161A22]">{o.name}</span>
            </button>
            <button
              type="button"
              data-testid={`people-picker-involve-${unaliasId(o.key)}`}
              aria-label={involvedKeys.has(o.key) ? "Remove from involved" : "Add to involved"}
              onClick={() => onToggleInvolved(o.key)}
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border text-[9px] leading-none ${
                involvedKeys.has(o.key)
                  ? "border-[#4F7DB3] bg-[#4F7DB3] text-white"
                  : "border-[#C7CEDA] bg-white text-transparent"
              }`}>
              {"✓"}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// Small overlapping avatar cluster for involved_people, rendered at the row's right edge.
const InvolvedCluster = ({ people }: { people: { avatar: string; name: string }[] }) => {
  if (!people.length) return null
  return (
    <div className="flex flex-shrink-0 items-center">
      {people.slice(0, 4).map((p, i) => (
        <div key={i} className={i > 0 ? "-ml-1.5" : ""}>
          <PeopleAvatar
            avatar={p.avatar}
            name={p.name}
            size={20}
            className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border border-white bg-[#EAF2FC] text-[9px] font-semibold text-[#4F7DB3]"
            fallbackClassName="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border border-white bg-[#EAF2FC] text-[9px] font-semibold text-[#4F7DB3]"
          />
        </div>
      ))}
      {people.length > 4 && (
        <span className="ml-1 text-[10.5px] text-[#9AA2B1]">+{people.length - 4}</span>
      )}
    </div>
  )
}

// ---- Agenda --------------------------------------------------------------------------------
const PURPOSE_LABEL: Record<string, string> = {
  inform: "Inform",
  discuss: "Discuss",
  decide: "Decide",
}

const AgendaRow = ({
  item,
  participantsByPid,
  peopleOptions,
  onToggleCovered,
  onSetOwner,
  onToggleInvolved,
}: {
  item: any
  participantsByPid: Record<string, any>
  peopleOptions: PickerOption[]
  onToggleCovered: (item: any) => void
  onSetOwner: (item: any, ownerKey: number | null) => void
  onToggleInvolved: (item: any, key: number) => void
}) => {
  const { avatar, name } = avatarProps(participantsByPid, item.owner_participant_id)
  const covered = !!item.discussed_at
  const [pickerOpen, setPickerOpen] = useState(false)
  const currentOwnerKey = item.owner_participant_id ? aliasId(item.owner_participant_id) : null
  const involvedKeys = useMemo(
    () => new Set((item.involved_people || []).map((id: string) => aliasId(id))),
    [item.involved_people]
  )
  const involvedPeople = useMemo(
    () =>
      peopleOptions.filter(o => involvedKeys.has(o.key)).map(o => ({ avatar: o.avatar, name: o.name })),
    [peopleOptions, involvedKeys]
  )
  return (
    <div
      data-testid="agenda-item-row"
      className="flex items-center gap-2.5 border-b border-[#F0F2F6] py-2 last:border-b-0">
      <button
        type="button"
        aria-label={covered ? "Mark not covered" : "Mark covered"}
        onClick={() => onToggleCovered(item)}
        className={`h-4 w-4 flex-shrink-0 rounded-full border ${
          covered ? "border-[#4F7DB3] bg-[#4F7DB3]" : "border-[#C7CEDA] bg-white"
        }`}
      />
      <span
        className={`flex-1 text-[13.5px] ${
          covered ? "text-[#9AA2B1] line-through" : "text-[#161A22]"
        }`}>
        {item.title}
      </span>
      {item.purpose && (
        <span className="rounded-full bg-[#F5F7FA] px-2 py-0.5 text-[11px] font-medium text-[#586173]">
          {PURPOSE_LABEL[item.purpose] || item.purpose}
        </span>
      )}
      {typeof item.minutes === "number" && (
        <span className="text-[11.5px] text-[#9AA2B1]">{item.minutes}m</span>
      )}
      <InvolvedCluster people={involvedPeople} />
      <div className="relative">
        {avatar || name ? (
          <button
            type="button"
            data-testid="agenda-row-assign"
            onClick={() => setPickerOpen(o => !o)}
            className="flex items-center gap-1.5">
            <PeopleAvatar
              avatar={avatar}
              name={name}
              size={24}
              className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-[#EAF2FC] text-[10px] font-semibold text-[#4F7DB3]"
            />
            <span className="text-[11px] font-semibold text-[#586173]">{name}</span>
          </button>
        ) : (
          <button
            type="button"
            data-testid="agenda-row-assign"
            onClick={() => setPickerOpen(o => !o)}
            className="rounded-full border border-[#E6EBF3] px-2.5 py-1 text-[11.5px] font-medium text-[#586173] hover:bg-[#F5F7FA]">
            Assign
          </button>
        )}
        {pickerOpen && (
          <PeoplePickerPopover
            options={peopleOptions}
            currentOwnerKey={currentOwnerKey}
            involvedKeys={involvedKeys}
            onSelectOwner={key => onSetOwner(item, key)}
            onClear={() => onSetOwner(item, null)}
            onToggleInvolved={key => onToggleInvolved(item, key)}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
    </div>
  )
}

const AgendaAddForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (fields: { title: string; purpose?: string; minutes?: number }) => void
  onCancel: () => void
}) => {
  const [title, setTitle] = useState("")
  const [purpose, setPurpose] = useState("discuss")
  const [minutes, setMinutes] = useState("")

  return (
    <form
      data-testid="agenda-add-form"
      className="flex flex-col gap-2"
      onSubmit={e => {
        e.preventDefault()
        if (!title.trim()) return
        onSubmit({
          title: title.trim(),
          purpose,
          minutes: minutes ? Number(minutes) : undefined,
        })
        setTitle("")
        setMinutes("")
      }}>
      <input
        data-testid="agenda-add-title"
        autoFocus
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Topic title"
        className="rounded-lg border border-[#E6EBF3] px-3 py-2 text-[13.5px] outline-none focus:border-[#7FB1E3]"
      />
      <div className="flex items-center gap-2">
        <select
          data-testid="agenda-add-purpose"
          value={purpose}
          onChange={e => setPurpose(e.target.value)}
          className="rounded-lg border border-[#E6EBF3] px-2 py-1.5 text-[12.5px]">
          <option value="inform">Inform</option>
          <option value="discuss">Discuss</option>
          <option value="decide">Decide</option>
        </select>
        <input
          data-testid="agenda-add-minutes"
          type="number"
          min={0}
          value={minutes}
          onChange={e => setMinutes(e.target.value)}
          placeholder="Min"
          className="w-16 rounded-lg border border-[#E6EBF3] px-2 py-1.5 text-[12.5px]"
        />
        <button
          type="submit"
          className="rounded-full bg-[#4F7DB3] px-3.5 py-1.5 text-[12.5px] font-semibold text-white">
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full px-3 py-1.5 text-[12.5px] font-medium text-[#586173]">
          Cancel
        </button>
      </div>
    </form>
  )
}

export const AgendaSection = () => {
  const dispatch = useDispatch<any>()
  const capable = useSelector(selectAgendaCapable)
  const teeupId = useSelector(selectActiveTeeupId)
  const items = useSelector(selectActiveTeeupAgendaItems)
  const participants = useSelector(selectTeeupPeople)
  const [adding, setAdding] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    dispatch(ensureCapabilitiesChecked())
  }, [dispatch])

  useEffect(() => {
    if (capable && teeupId) dispatch(loadAgendaItems(teeupId))
  }, [capable, teeupId, dispatch])

  // Agenda people refs (owner_participant_id/involved_people) are participants.id -- a DIFFERENT
  // id space than user_id, so the picker needs a participantId-keyed lookup + option list built
  // off it (not the user-id-keyed `participants` map used directly).
  const participantsByPid = useMemo(() => {
    const m: Record<string, any> = {}
    Object.values(participants).forEach((p: any) => {
      if (p && p.participantId != null) m[p.participantId] = p
    })
    return m
  }, [participants])
  const peopleOptions: PickerOption[] = useMemo(
    () =>
      Object.values(participants)
        .filter((p: any) => p && p.participantId != null)
        .map((p: any) => ({ key: p.participantId, avatar: p.avatar || "", name: p.name || "" })),
    [participants]
  )

  // v11 total header chip (ruling R-1): "~Nm total · times approximate", rendered only when at
  // least one item carries a numeric `minutes` value (v11's empty-state header shows no chip).
  // NO derived clock times / When-anchoring -- this is a pure sum of the existing per-row chips.
  // HOOKS-ORDER FIX (2026-07-10, pre-existing crasher independent of chain-4 U2): this useMemo
  // used to sit AFTER the `if (!capable) return null` gate below, so on the render where
  // `capable` flips false->true (once the capabilities fetch resolves) the component suddenly
  // calls ONE MORE hook than it did on the previous render -- a Rules-of-Hooks violation that
  // throws React error #310 ("Rendered more hooks than during the previous render") and trips
  // the page's error boundary ("something went wrong") on every real TeeUp-detail load under the
  // dev backend, since `capable` always starts false pre-fetch. Hoisted above the early return so
  // every hook in this component runs unconditionally, every render, per the Rules of Hooks.
  const totalMinutes = useMemo(() => {
    let sum = 0
    let any = false
    items.forEach(i => {
      if (typeof i.minutes === "number") {
        sum += i.minutes
        any = true
      }
    })
    return any ? sum : null
  }, [items])

  // Gate: nothing renders until capabilities says agenda is on. This never blocks the rest of
  // the page -- the effects above are fire-and-forget, never awaited by a parent.
  if (!capable) return null

  return (
    <div data-testid="teeup-detail-agenda">
      <div className="mb-2 mt-6 flex items-center justify-between px-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9AA2B1]">
          Agenda
        </span>
        <div className="flex items-center gap-2">
          {totalMinutes != null && (
            <span
              data-testid="agenda-total-chip"
              className="text-[11.5px] text-[#9AA2B1]">
              {`~${totalMinutes}m total · times approximate`}
            </span>
          )}
          {typeof items.length === "number" && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9AA2B1]">
              {items.length}
            </span>
          )}
        </div>
      </div>
      <Card testId="teeup-detail-agenda-card">
        {notice && (
          <div role="status" className="mb-2 rounded-lg bg-[#FBEFEA] px-3 py-2 text-[12px] text-[#C0603B]">
            {notice}
          </div>
        )}
        {items.length === 0 && !adding && (
          <div className="flex flex-col items-start gap-3">
            <div className="text-[13.5px] text-[#9AA2B1]">No agenda items yet.</div>
            <AddButton
              testId="teeup-detail-agenda-add"
              label="+ Add agenda item"
              onClick={() => setAdding(true)}
            />
          </div>
        )}
        {items.length > 0 && (
          <div>
            {items.map(item => (
              <AgendaRow
                key={item.id}
                item={item}
                participantsByPid={participantsByPid}
                peopleOptions={peopleOptions}
                onToggleCovered={i =>
                  dispatch(markAgendaItemCovered(teeupId, i))
                }
                onSetOwner={(i, ownerKey) =>
                  dispatch(
                    setAgendaItemPeople(
                      teeupId,
                      i.id,
                      { owner_participant_id: ownerKey == null ? null : unaliasId(ownerKey) },
                      i.version,
                      {
                        onConflict: () =>
                          setNotice("Someone else updated this item -- refreshed with the latest."),
                      }
                    )
                  )
                }
                onToggleInvolved={(i, key) => {
                  const current = new Set((i.involved_people || []).map((id: string) => aliasId(id)))
                  if (current.has(key)) current.delete(key)
                  else current.add(key)
                  dispatch(
                    setAgendaItemPeople(
                      teeupId,
                      i.id,
                      { involved_people: Array.from(current).map(k => unaliasId(k as number)) },
                      i.version,
                      {
                        onConflict: () =>
                          setNotice("Someone else updated this item -- refreshed with the latest."),
                      }
                    )
                  )
                }}
              />
            ))}
            {!adding && (
              <div className="pt-2">
                <AddButton
                  testId="teeup-detail-agenda-add"
                  label="+ Add agenda item"
                  onClick={() => setAdding(true)}
                />
              </div>
            )}
          </div>
        )}
        {adding && (
          <div className={items.length > 0 ? "pt-2" : ""}>
            <AgendaAddForm
              onSubmit={fields => {
                dispatch(addAgendaItem(teeupId, fields))
                setAdding(false)
              }}
              onCancel={() => setAdding(false)}
            />
          </div>
        )}
      </Card>
    </div>
  )
}

// ---- Tasks ---------------------------------------------------------------------------------
const PHASE_LABEL: Record<string, string> = {
  before: "Before",
  day_of: "Day of",
  after: "After",
}
const PHASE_ORDER = ["before", "day_of", "after"]

// R-2: prefer the backend's computed `due_label` verbatim when present (offset WORDS -- "day
// before", "Nh before", "Nh after", or an absolute "Jul 9" per the locked enum). Fall back to the
// existing local computation only for older payloads that don't carry due_label yet.
const dueLabel = (task: any) => {
  if (typeof task.due_label === "string" && task.due_label) {
    return task.due_label
  }
  if (task.due_at) {
    const d = new Date(task.due_at)
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    }
  }
  if (task.due_source === "derived" && typeof task.due_offset_minutes === "number") {
    const hrs = Math.round(task.due_offset_minutes / 60)
    return task.due_offset_minutes < 0 ? `${Math.abs(hrs)}h before` : `${hrs}h after`
  }
  return null
}

// R-6: attachment affordance -- a small paperclip SLOT, always rendered but disabled. No upload
// backend, no click handler doing anything.
const AttachmentSlot = () => (
  <span
    data-testid="task-row-attachment-slot"
    aria-disabled="true"
    title="Attachments coming soon"
    className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-[#C7CEDA]">
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 01-7.78-7.78l9.19-9.19a3.5 3.5 0 014.95 4.95l-9.2 9.19a1.5 1.5 0 01-2.12-2.12l8.49-8.48" />
    </svg>
  </span>
)

const TaskRow = ({
  task,
  participants,
  peopleOptions,
  currentUserId,
  onToggleComplete,
  onClaim,
  onSetAssignee,
  onToggleInvolved,
}: {
  task: any
  participants: Record<string, any>
  peopleOptions: PickerOption[]
  currentUserId: any
  onToggleComplete: (task: any) => void
  onClaim: (task: any) => void
  onSetAssignee: (task: any, key: number | null) => void
  onToggleInvolved: (task: any, key: number) => void
}) => {
  const { avatar, name } = avatarProps(participants, task.assignee_id)
  const complete = !!task.completed_at
  const assignedToMe = task.assignee_id && aliasId(task.assignee_id) === currentUserId
  const due = dueLabel(task)
  const [pickerOpen, setPickerOpen] = useState(false)
  const currentOwnerKey = task.assignee_id ? aliasId(task.assignee_id) : null
  const involvedKeys = useMemo(
    () => new Set((task.involved_people || []).map((id: string) => aliasId(id))),
    [task.involved_people]
  )
  const involvedPeople = useMemo(
    () =>
      peopleOptions.filter(o => involvedKeys.has(o.key)).map(o => ({ avatar: o.avatar, name: o.name })),
    [peopleOptions, involvedKeys]
  )
  return (
    <div
      data-testid="task-row"
      className="flex items-center gap-2.5 border-b border-[#F0F2F6] py-2 last:border-b-0">
      <input
        type="checkbox"
        checked={complete}
        onChange={() => onToggleComplete(task)}
        aria-label={complete ? "Mark incomplete" : "Mark complete"}
        data-testid="task-row-complete"
        className="h-4 w-4 flex-shrink-0 accent-[#4F7DB3]"
      />
      <span
        className={`flex-1 text-[13.5px] ${
          complete ? "text-[#9AA2B1] line-through" : "text-[#161A22]"
        }`}>
        {task.title}
        {assignedToMe && !complete && (
          <span className="ml-1.5 text-[11px] font-semibold text-[#4F7DB3]">(you)</span>
        )}
      </span>
      {task.consequence && !complete && (
        <span
          data-testid="task-row-reschedule-shield"
          title="Not done by the deadline reschedules this TeeUp"
          className="rounded-full bg-[#FBEFEA] px-2 py-0.5 text-[11px] font-medium text-[#C0603B]">
          Reschedule risk
        </span>
      )}
      {due && <span className="text-[11.5px] text-[#9AA2B1]">{due}</span>}
      <AttachmentSlot />
      <InvolvedCluster people={involvedPeople} />
      <div className="relative flex items-center gap-1.5">
        {task.assignee_id ? (
          <button
            type="button"
            data-testid="task-row-assign"
            onClick={() => setPickerOpen(o => !o)}
            className="flex items-center gap-1.5">
            <PeopleAvatar
              avatar={avatar}
              name={name}
              size={24}
              className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-[#EAF2FC] text-[10px] font-semibold text-[#4F7DB3]"
            />
            <span className="text-[11px] font-semibold text-[#586173]">{name}</span>
          </button>
        ) : (
          <>
            <button
              type="button"
              data-testid="task-row-claim"
              onClick={() => onClaim(task)}
              className="rounded-full border border-[#E6EBF3] px-2.5 py-1 text-[11.5px] font-medium text-[#586173] hover:bg-[#F5F7FA]">
              I&apos;ll do it
            </button>
            <button
              type="button"
              data-testid="task-row-assign"
              onClick={() => setPickerOpen(o => !o)}
              className="rounded-full border border-[#E6EBF3] px-2.5 py-1 text-[11.5px] font-medium text-[#586173] hover:bg-[#F5F7FA]">
              Assign
            </button>
          </>
        )}
        {pickerOpen && (
          <PeoplePickerPopover
            options={peopleOptions}
            currentOwnerKey={currentOwnerKey}
            involvedKeys={involvedKeys}
            onSelectOwner={key => onSetAssignee(task, key)}
            onClear={() => onSetAssignee(task, null)}
            onToggleInvolved={key => onToggleInvolved(task, key)}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
    </div>
  )
}

const TaskAddForm = ({
  onSubmit,
  onCancel,
  peopleOptions,
}: {
  onSubmit: (fields: {
    title: string
    phase: string
    assignee_id?: string
    due_expression?: string
    due_at?: string
    consequence?: string
  }) => void
  onCancel: () => void
  peopleOptions: PickerOption[]
}) => {
  const [title, setTitle] = useState("")
  const [phase, setPhase] = useState("before")
  const [pickerOpen, setPickerOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState<number | null>(null)
  const [dueChoice, setDueChoice] = useState("none")
  const [dueDate, setDueDate] = useState("")
  const [consequenceChecked, setConsequenceChecked] = useState(false)

  // Look up the selected person's display name for the chip.
  const selectedPerson = useMemo(
    () => peopleOptions.find(o => o.key === selectedKey) ?? null,
    [peopleOptions, selectedKey],
  )

  return (
    <div className="relative">
      <form
        data-testid="task-add-form"
        className="flex flex-col gap-2"
        onSubmit={e => {
          e.preventDefault()
          if (!title.trim()) return
          const fields: {
            title: string
            phase: string
            assignee_id?: string
            due_expression?: string
            due_at?: string
            consequence?: string
          } = {
            title: title.trim(),
            phase,
          }
          if (selectedKey != null) {
            fields.assignee_id = unaliasId(selectedKey)
          }
          if (dueChoice === "day_before") {
            fields.due_expression = "day_before"
          } else if (dueChoice === "absolute" && dueDate) {
            fields.due_expression = "absolute"
            // Local midnight of the picked date -> ISO (the date input yields YYYY-MM-DD).
            fields.due_at = new Date(`${dueDate}T00:00:00`).toISOString()
          }
          if (consequenceChecked) {
            fields.consequence = "reschedule_if_undone"
          }
          onSubmit(fields)
          setTitle("")
          setSelectedKey(null)
          setDueChoice("none")
          setDueDate("")
          setConsequenceChecked(false)
        }}>
        <input
          data-testid="task-add-title"
          autoFocus
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Task title"
          className="rounded-lg border border-[#E6EBF3] px-3 py-2 text-[13.5px] outline-none focus:border-[#7FB1E3]"
        />
        <div className="flex items-center gap-2">
          <select
            data-testid="task-add-due"
            value={dueChoice}
            onChange={e => setDueChoice(e.target.value)}
            className="rounded-lg border border-[#E6EBF3] px-2 py-1.5 text-[12.5px]">
            <option value="none">No deadline</option>
            <option value="day_before">Day before the TeeUp</option>
            <option value="absolute">Exact date</option>
          </select>
          {dueChoice === "absolute" && (
            <input
              type="date"
              data-testid="task-add-due-date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="rounded-lg border border-[#E6EBF3] px-2 py-1.5 text-[12.5px]"
            />
          )}
          <label className="flex items-center gap-1.5 text-[12.5px] text-[#586173]">
            <input
              type="checkbox"
              data-testid="task-add-consequence"
              checked={consequenceChecked}
              onChange={e => setConsequenceChecked(e.target.checked)}
            />
            Reschedule if not done
          </label>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="task-add-phase"
            value={phase}
            onChange={e => setPhase(e.target.value)}
            className="rounded-lg border border-[#E6EBF3] px-2 py-1.5 text-[12.5px]">
            <option value="before">Before</option>
            <option value="day_of">Day of</option>
            <option value="after">After</option>
          </select>
          <button
            type="button"
            data-testid="task-add-assign"
            onClick={() => setPickerOpen(o => !o)}
            className="rounded-full border border-[#7FB1E3] bg-[#EAF2FC] px-3.5 py-1.5 text-[13px] font-semibold text-[#4F7DB3] hover:bg-[#DCEAFA]">
            Assign
          </button>
          <button
            type="submit"
            className="rounded-full bg-[#4F7DB3] px-3.5 py-1.5 text-[12.5px] font-semibold text-white">
            Add
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full px-3 py-1.5 text-[12.5px] font-medium text-[#586173]">
            Cancel
          </button>
        </div>
      </form>

      {/* Selected person chip */}
      {selectedPerson && (
        <div className="mt-1 flex items-center gap-1.5 rounded-full bg-[#EAF2FC] px-2 py-0.5">
          <PeopleAvatar
            avatar={selectedPerson.avatar}
            name={selectedPerson.name}
            size={16}
            className="flex h-4 w-4 items-center justify-center overflow-hidden rounded-full bg-[#DCEAFA] text-[8px] font-semibold text-[#4F7DB3]"
          />
          <span className="text-[11.5px] text-[#4F7DB3]">{selectedPerson.name}</span>
        </div>
      )}

      {pickerOpen && (
        <PeoplePickerPopover
          options={peopleOptions}
          currentOwnerKey={null}
          involvedKeys={new Set()}
          onSelectOwner={key => setSelectedKey(key)}
          onClear={() => setSelectedKey(null)}
          onToggleInvolved={() => {}}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}

// WU-P2-FE-b3 (2026-07-09) audit, rulings R-4 + R-8: counters and chrome checked against the
// redline (P0-redline-agenda-tasks-2026-07-09.md Sections 1/2) and the planner rulings
// (P0-planner-rulings-2026-07-09.md). Findings, no code change required:
//   - R-4: the "Tasks n/m" fraction is explicitly NOT Target-1 (unverified source image). The
//     SectionHeader count below already derives from `tasks.length` (the store-backed, filtered
//     array), so it stays a single count and updates automatically on add/complete/filter-toggle
//     -- already correct, no change needed.
//   - R-8: inline-card chrome must match the SHIPPED sibling tokens (13px radius, round checkbox,
//     blue accent -- Card/TaskRow/AgendaRow above), NOT v11's tokens (10px radius, square check,
//     green). The redline's Section 1/2 "Target-1 action" cells name no concrete Agenda/Tasks
//     chrome change (empty states, purpose tags, phase dividers, avatar chips all already match);
//     v11 token migration is explicitly out of scope (R-8, Target 2). No visual change invented.
// b3 audited: counters correct (single counts per R-4), chrome per R-8 sibling tokens -- no
// change required.
export const TasksSection = () => {
  const dispatch = useDispatch<any>()
  const capable = useSelector(selectTasksCapable)
  const teeupId = useSelector(selectActiveTeeupId)
  const allTasks = useSelector(selectActiveTeeupTasks)
  const participants = useSelector(selectTeeupPeople)
  const currentUserId = useSelector(selectUserId)
  const [filter, setFilter] = useState<"all" | "me">("all")
  const [adding, setAdding] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    dispatch(ensureCapabilitiesChecked())
  }, [dispatch])

  useEffect(() => {
    if (capable && teeupId) dispatch(loadTasks(teeupId))
  }, [capable, teeupId, dispatch])

  const tasks = useMemo(
    () =>
      filter === "me"
        ? allTasks.filter(t => t.assignee_id && aliasId(t.assignee_id) === currentUserId)
        : allTasks,
    [allTasks, filter, currentUserId]
  )

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = { before: [], day_of: [], after: [] }
    tasks.forEach(t => {
      const p = PHASE_ORDER.includes(t.phase) ? t.phase : "before"
      g[p].push(t)
    })
    return g
  }, [tasks])

  // Task people refs (assignee_id/involved_people) are participants.user_id -- the SAME id space
  // `participants` is already keyed by, unlike agenda's participants.id.
  const peopleOptions: PickerOption[] = useMemo(
    () =>
      Object.values(participants)
        .filter((p: any) => p && p.id != null)
        .map((p: any) => ({ key: p.id, avatar: p.avatar || "", name: p.name || "" })),
    [participants]
  )

  if (!capable) return null

  const pillClass = (active: boolean) =>
    `rounded-full border px-3.5 py-1 text-[12.5px] font-medium ${
      active
        ? "border-transparent bg-[#EAF2FC] text-[#161A22]"
        : "border-[#E6EBF3] bg-white text-[#586173]"
    }`

  return (
    <div data-testid="teeup-detail-tasks">
      <SectionHeader label="To-Dos" count={tasks.length} />
      <div className="mb-2 flex gap-2 px-1" data-testid="teeup-detail-tasks-filter">
        <button
          type="button"
          data-testid="teeup-detail-tasks-filter-all"
          onClick={() => setFilter("all")}
          className={pillClass(filter === "all")}>
          All
        </button>
        <button
          type="button"
          data-testid="teeup-detail-tasks-filter-me"
          onClick={() => setFilter("me")}
          className={pillClass(filter === "me")}>
          Me
        </button>
      </div>
      <Card testId="teeup-detail-tasks-card">
        {notice && (
          <div role="status" className="mb-2 rounded-lg bg-[#FBEFEA] px-3 py-2 text-[12px] text-[#C0603B]">
            {notice}
          </div>
        )}
        {tasks.length === 0 && !adding && (
          <div className="flex flex-col items-start gap-3">
            <div className="text-[13.5px] text-[#9AA2B1]">
              {filter === "me" ? "Nothing assigned to you." : "No tasks yet."}
            </div>
            <AddButton
              testId="teeup-detail-tasks-add"
              label="+ Add task"
              onClick={() => setAdding(true)}
            />
          </div>
        )}
        {tasks.length > 0 && (
          <div>
            {PHASE_ORDER.filter(p => grouped[p].length > 0).map(phase => (
              <div key={phase} className="mb-1">
                <div className="pb-1 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[#9AA2B1]">
                  {PHASE_LABEL[phase]}
                </div>
                {grouped[phase].map(task => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    participants={participants}
                    peopleOptions={peopleOptions}
                    currentUserId={currentUserId}
                    onToggleComplete={t =>
                      dispatch(
                        toggleTaskComplete(teeupId, t, {
                          onConflict: () =>
                            setNotice("Someone else updated this task -- refreshed with the latest."),
                        })
                      )
                    }
                    onClaim={t =>
                      dispatch(
                        claimTaskThunk(teeupId, t.id, {
                          onClaimLost: () => setNotice("Already claimed by someone else."),
                        })
                      )
                    }
                    onSetAssignee={(t, key) =>
                      dispatch(
                        setTaskPeople(
                          teeupId,
                          t.id,
                          { assignee_id: key == null ? null : unaliasId(key) },
                          t.version,
                          {
                            onConflict: () =>
                              setNotice("Someone else updated this task -- refreshed with the latest."),
                          }
                        )
                      )
                    }
                    onToggleInvolved={(t, key) => {
                      const current = new Set((t.involved_people || []).map((id: string) => aliasId(id)))
                      if (current.has(key)) current.delete(key)
                      else current.add(key)
                      dispatch(
                        setTaskPeople(
                          teeupId,
                          t.id,
                          { involved_people: Array.from(current).map(k => unaliasId(k as number)) },
                          t.version,
                          {
                            onConflict: () =>
                              setNotice("Someone else updated this task -- refreshed with the latest."),
                          }
                        )
                      )
                    }}
                  />
                ))}
              </div>
            ))}
            {!adding && (
              <div className="pt-2">
                <AddButton
                  testId="teeup-detail-tasks-add"
                  label="+ Add task"
                  onClick={() => setAdding(true)}
                />
              </div>
            )}
          </div>
        )}
        {adding && (
          <div className={tasks.length > 0 ? "pt-2" : ""}>
            <TaskAddForm
              onSubmit={fields => {
                dispatch(addTask(teeupId, fields))
                setAdding(false)
              }}
              onCancel={() => setAdding(false)}
              peopleOptions={peopleOptions}
            />
          </div>
        )}
      </Card>
    </div>
  )
}
